import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { QRCodeSVG } from "qrcode.react";
import { Loader2, Maximize2, Minimize2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatDate(d) {
  if (!d) return "";
  try {
    const [y, m, day] = d.split("-").map((s) => parseInt(s, 10));
    if (!y || !m || !day) return d;
    return new Date(y, m - 1, day).toLocaleDateString(undefined, { weekday: "long", year: "numeric", month: "long", day: "numeric" });
  } catch { return d; }
}

function formatTime(t) {
  if (!t) return "";
  try {
    const [h, m] = t.split(":");
    const hh = parseInt(h, 10);
    if (isNaN(hh)) return t;
    const period = hh >= 12 ? "PM" : "AM";
    const display = ((hh + 11) % 12) + 1;
    return `${display}:${m || "00"} ${period}`;
  } catch { return t; }
}

function looksLikeUrl(value) {
  const v = (value || "").trim();
  if (!v) return false;
  try {
    const u = new URL(v);
    return u.protocol === "http:" || u.protocol === "https:";
  } catch {
    return false;
  }
}

export default function EventSurveyDisplay() {
  const { id } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [isFs, setIsFs] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/public/global-events/${id}`);
      setEvent(data);
    } catch {
      setNotFound(true);
    } finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // Track fullscreen state for the toggle button label
  useEffect(() => {
    const onFs = () => setIsFs(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onFs);
    return () => document.removeEventListener("fullscreenchange", onFs);
  }, []);

  const toggleFullscreen = async () => {
    try {
      if (!document.fullscreenElement) {
        await document.documentElement.requestFullscreen();
      } else {
        await document.exitFullscreen();
      }
    } catch {
      /* ignored — some browsers block without user gesture */
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (notFound || !event) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900 text-center px-6">
        <div>
          <h1 className="font-heading text-3xl font-bold text-white">Event not found</h1>
          <p className="mt-2 text-base text-slate-400 font-body">This survey link is invalid or has been removed.</p>
        </div>
      </div>
    );
  }

  const url = (event.survey_url || "").trim();
  const isValid = looksLikeUrl(url);

  return (
    <div className="min-h-screen bg-white text-slate-900 flex flex-col" data-testid="event-survey-display-root">
      {/* Fullscreen toggle — sits in the corner, fades into the background */}
      <button
        onClick={toggleFullscreen}
        className="fixed top-4 right-4 z-20 inline-flex items-center gap-1.5 text-xs font-body text-slate-400 hover:text-slate-700 bg-white/80 backdrop-blur border border-slate-200 rounded-full px-3 py-1.5 shadow-sm"
        data-testid="event-survey-display-fullscreen"
        title={isFs ? "Exit fullscreen" : "Fullscreen — best for TV / projector"}
      >
        {isFs ? <><Minimize2 className="h-3.5 w-3.5" /> Exit</> : <><Maximize2 className="h-3.5 w-3.5" /> Fullscreen</>}
      </button>

      {/* Main content — vertically centered, scales for TV viewing distance */}
      <div className="flex-1 flex items-center justify-center px-6 py-10 sm:py-16">
        <div className="w-full max-w-5xl text-center">
          <p
            className="text-xs sm:text-sm uppercase tracking-[0.3em] text-slate-500 font-body"
            data-testid="event-survey-display-eyebrow"
          >
            Thank you for attending
          </p>
          <h1
            className="font-heading text-4xl sm:text-6xl lg:text-7xl font-bold mt-4 sm:mt-6 text-slate-900 leading-[1.05] tracking-tight"
            data-testid="event-survey-display-name"
          >
            {event.name}
          </h1>
          <div className="mt-5 sm:mt-8 inline-flex flex-wrap items-center justify-center gap-x-8 gap-y-2 text-base sm:text-xl text-slate-600 font-body">
            {event.date ? <span>{formatDate(event.date)}</span> : null}
            {event.start_time ? <span>{formatTime(event.start_time)}</span> : null}
            {event.location ? <span>{event.location}</span> : null}
          </div>

          {/* QR + scan instruction */}
          <div className="mt-10 sm:mt-14 flex flex-col items-center gap-5">
            {isValid ? (
              <>
                <div
                  className="p-5 sm:p-7 bg-white border-4 border-slate-200 rounded-2xl shadow-sm"
                  data-testid="event-survey-display-qr"
                >
                  <QRCodeSVG
                    value={url}
                    size={420}
                    level="M"
                    includeMargin={false}
                    bgColor="#FFFFFF"
                    fgColor="#0A0C10"
                    className="w-[260px] h-[260px] sm:w-[360px] sm:h-[360px] lg:w-[440px] lg:h-[440px]"
                  />
                </div>
                <p className="text-sm sm:text-lg uppercase tracking-[0.25em] text-slate-500 font-body">
                  Scan to open the survey
                </p>
                <p className="text-sm sm:text-base text-slate-500 font-body max-w-md">
                  Open your phone's camera and point it at the code, then tap the link that appears.
                </p>
              </>
            ) : (
              <div
                className="rounded-xl border-2 border-dashed border-slate-300 px-10 py-16 text-center"
                data-testid="event-survey-display-placeholder"
              >
                <p className="font-heading text-xl font-semibold text-slate-700">Survey not configured yet</p>
                <p className="text-sm font-body text-slate-500 mt-2">The organizer hasn't added a survey link for this event.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
